import {
  isRecruiterLanguage,
  type RecruiterLanguage,
} from 'recruiter-i18n/languages';
import { sessionStore, SessionStorageKey, storage } from 'utils/typedStorage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// localStorage keys
const STORAGE_KEYS = {
  IS_SUPPORT: 'isSupport',
  FULL_ACCESS_ENABLED: 'fullAccessEnabled',
  IMPERSONATED_EMAIL: 'email',
} as const;

interface SupportUserState {
  /**
   * Whether the server has authorized the current session for support access.
   */
  isSupportUser: boolean;

  /**
   * Whether Full Access mode is enabled.
   * - true = Support user has Admin permissions in the impersonated user's org
   * - false = Support user sees exactly what the impersonated user sees (their actual permissions)
   */
  fullAccessEnabled: boolean;

  /**
   * The email of the user being impersonated.
   * This is always set when a support user is operating in an org context.
   */
  impersonatedEmail: string | null;

  /** Tab-local display override for existing translations, independent of the mocked user. */
  uiLanguageOverride: RecruiterLanguage | null;
  setUiLanguageOverride: (language: RecruiterLanguage | null) => void;

  // Actions
  /**
   * Mark the current session as a support user session.
   * Called only after the server confirms support access.
   */
  setSupportUser: () => void;

  /**
   * Set the email of the user being impersonated.
   * Called when a support user selects a user to impersonate.
   */
  setImpersonatedEmail: (email: string) => void;

  /**
   * Toggle Full Access mode on/off.
   * When enabled, support user gets Admin permissions.
   * When disabled, support user sees the impersonated user's actual permissions.
   */
  toggleFullAccess: () => void;

  /**
   * Enable Full Access mode explicitly.
   */
  enableFullAccess: () => void;

  /**
   * Disable Full Access mode explicitly.
   */
  disableFullAccess: () => void;

  /**
   * Reset all support user state (e.g., on logout or when switching users).
   */
  reset: () => void;

  /**
   * Hydrate the store from localStorage.
   * Called on app initialization.
   */
  hydrate: () => void;
}

function getStorageItem(key: string): string | null {
  return storage.getDynamic(key);
}

function setStorageItem(key: string, value: string): void {
  storage.setDynamic(key, value);
}

function removeStorageItem(key: string): void {
  storage.removeDynamic(key);
}

/**
 * Zustand store for support user state.
 *
 * This is the single source of truth for support user state, replacing
 * the scattered localStorage checks throughout the codebase.
 *
 * Benefits:
 * - Positive logic (fullAccessEnabled=true means Full Access is ON)
 * - Single place to read/write support state
 * - Components can subscribe to state changes
 * - Syncs with localStorage for persistence across page reloads
 */
export const useSupportUserStore = create<SupportUserState>()(
  devtools(
    (set, get) => ({
      isSupportUser: false,
      fullAccessEnabled: false,
      impersonatedEmail: null,
      uiLanguageOverride: null,

      setUiLanguageOverride: (language) => {
        if (language !== null && !get().isSupportUser) return;
        if (language === null) {
          sessionStore.remove(SessionStorageKey.SupportUiLanguage);
        } else {
          sessionStore.set(SessionStorageKey.SupportUiLanguage, language);
        }
        set({ uiLanguageOverride: language });
      },

      setSupportUser: () => {
        setStorageItem(STORAGE_KEYS.IS_SUPPORT, 'true');
        // Default to impersonation mode (Full Access OFF) when first becoming a support user
        setStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED, 'false');
        set({ isSupportUser: true, fullAccessEnabled: false });
      },

      setImpersonatedEmail: (email: string) => {
        setStorageItem(STORAGE_KEYS.IMPERSONATED_EMAIL, email);
        set({ impersonatedEmail: email });
      },

      toggleFullAccess: () => {
        const newValue = !get().fullAccessEnabled;
        setStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED, String(newValue));
        set({ fullAccessEnabled: newValue });
      },

      enableFullAccess: () => {
        setStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED, 'true');
        set({ fullAccessEnabled: true });
      },

      disableFullAccess: () => {
        setStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED, 'false');
        set({ fullAccessEnabled: false });
      },

      reset: () => {
        sessionStore.remove(SessionStorageKey.SupportUiLanguage);
        removeStorageItem(STORAGE_KEYS.IS_SUPPORT);
        removeStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED);
        // Note: We don't remove IMPERSONATED_EMAIL here because it's shared with regular email storage
        set({
          isSupportUser: false,
          fullAccessEnabled: false,
          impersonatedEmail: null,
          uiLanguageOverride: null,
        });
      },

      hydrate: () => {
        const isSupport = getStorageItem(STORAGE_KEYS.IS_SUPPORT) === 'true';
        if (!isSupport) {
          sessionStore.remove(SessionStorageKey.SupportUiLanguage);
        }
        const override = sessionStore.get(SessionStorageKey.SupportUiLanguage);
        const impersonatedEmail = getStorageItem(
          STORAGE_KEYS.IMPERSONATED_EMAIL
        );
        const fullAccessEnabled =
          getStorageItem(STORAGE_KEYS.FULL_ACCESS_ENABLED) === 'true';

        set({
          isSupportUser: isSupport,
          fullAccessEnabled,
          impersonatedEmail: impersonatedEmail || null,
          uiLanguageOverride:
            isSupport && isRecruiterLanguage(override) ? override : null,
        });
      },
    }),
    { name: 'support-user-store' }
  )
);
